import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  type IBillData,
  type IBillDetail,
  type IDonationData,
  type IJointBillServicePeriodData,
  type IOverviewJointBillService,
} from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";
import {
  getBillCertificate,
  selectDonationBill,
  unselectDonationBill,
  updateBillDetailExpectedPaymentAmount,
  updateBillDetailSettings,
  updateBillDetailTariff,
  updateBillDetailValueFrom,
  updateBillDetailValueTo,
  updateBillNumber,
  updateBillSelection,
  updateBillTotalExpectedPaymentAmount,
  updateHouseBillsSelection,
} from "./services";
import {
  type IJBSPeriodBillDetailParams,
  type IJBSPeriodParams,
  type IUpdateBillDetailSettingsRequestParams,
} from "./types";

export const useUpdateBillSelectionMutation = ({
  jointBillServiceId,
  periodId,
}: IJBSPeriodParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["jointBillMutation", "updateBillSelection"],
    mutationFn: updateBillSelection,
    onSuccess: async (_, data) => {
      await Promise.all([
        queryClient.setQueryData<IJointBillServicePeriodData>(
          ["jointBillServicePeriodData", jointBillServiceId, periodId],
          (period) =>
            !isNullOrUndefined(period)
              ? {
                  ...period,
                  billData: period.billData.map((bd) => ({
                    ...bd,
                    ...(bd.billId === data.billId
                      ? {
                          isSelected: data.isSelected,
                        }
                      : {}),
                  })),
                }
              : period,
        ),
        queryClient.invalidateQueries({
          queryKey: [
            "jointBillServicePeriodData",
            jointBillServiceId,
            periodId,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicesExpectedPaymentAmount"],
        }),
      ]);
    },
  });
};

export const useUpdateHouseBillsSelectionMutation = ({
  jointBillServiceId,
  periodId,
}: IJBSPeriodParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["jointBillMutation", "updateHouseBillsSelection"],
    mutationFn: updateHouseBillsSelection,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            "jointBillServicePeriodData",
            jointBillServiceId,
            periodId,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicesExpectedPaymentAmount"],
        }),
      ]);
    },
  });
};

export const useUpdateTotalExpectedPaymentAmountMutation = ({
  jointBillServiceId,
  periodId,
}: IJBSPeriodParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["jointBillMutation", "updateTotalExpectedPaymentAmount"],
    mutationFn: updateBillTotalExpectedPaymentAmount,
    onSuccess: async () => {
      await Promise.all([
        //TODO: Mutate locally with endpoint response
        // queryClient.setQueryData<IJointBillServicePeriodData>(
        //   ["jointBillServicePeriodData", jointBillServiceId, periodId],
        //   period,
        // ),
        queryClient.invalidateQueries({
          queryKey: [
            "jointBillServicePeriodData",
            jointBillServiceId,
            periodId,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicesExpectedPaymentAmount"],
        }),
      ]);
    },
  });
};

export const useUpdateBillDetailValueFromMutation = ({
  jointBillServiceId,
  periodId,
  billDetailId,
}: IJBSPeriodBillDetailParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["jointBillMutation", "updateBillDetailValueFrom"],
    mutationFn: (value: number) =>
      updateBillDetailValueFrom(
        jointBillServiceId,
        periodId,
        billDetailId,
        value,
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            "jointBillServicePeriodData",
            jointBillServiceId,
            periodId,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicesExpectedPaymentAmount"],
        }),
      ]);
    },
  });
};

export const useUpdateBillDetailValueToMutation = ({
  jointBillServiceId,
  periodId,
  billDetailId,
}: IJBSPeriodBillDetailParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["jointBillMutation", "updateBillDetailValueTo"],
    mutationFn: (value: number) =>
      updateBillDetailValueTo(
        jointBillServiceId,
        periodId,
        billDetailId,
        value,
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            "jointBillServicePeriodData",
            jointBillServiceId,
            periodId,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicesExpectedPaymentAmount"],
        }),
      ]);
    },
  });
};

export const useUpdateBillDetailTariffMutation = ({
  jointBillServiceId,
  periodId,
  billDetailId,
}: IJBSPeriodBillDetailParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["jointBillMutation", "updateBillDetailsTariff"],
    mutationFn: (value: number) =>
      updateBillDetailTariff(jointBillServiceId, periodId, billDetailId, value),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            "jointBillServicePeriodData",
            jointBillServiceId,
            periodId,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicesExpectedPaymentAmount"],
        }),
      ]);
    },
  });
};

export const useUpdateBillDetailExpectedPaymentAmountMutation = ({
  jointBillServiceId,
  periodId,
  billDetailId,
}: IJBSPeriodBillDetailParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["jointBillMutation", "updateBillDetailExpectedPaymentAmount"],
    mutationFn: (value: number) =>
      updateBillDetailExpectedPaymentAmount(
        jointBillServiceId,
        billDetailId,
        periodId,
        value,
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            "jointBillServicePeriodData",
            jointBillServiceId,
            periodId,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicesExpectedPaymentAmount"],
        }),
      ]);
    },
  });
};

export const useUpdateBillDetailMeterSettingsMutation = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
  jointBillServiceAgreementId: IBillData["jointBillServiceAgreementId"],
  billDetailId: IBillDetail["billDetailId"],
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["jointBillMutation", "updateBillDetailMeterSettings"],
    mutationFn: (data: IUpdateBillDetailSettingsRequestParams) =>
      updateBillDetailSettings(
        jointBillServiceId,
        jointBillServiceAgreementId,
        billDetailId,
        data,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["jointBillServicePeriodData", jointBillServiceId],
      });
    },
  });
};

export const useSelectDonationBillMutation = ({
  jointBillServiceId,
  periodId,
  donationServiceProviderId,
}: {
  jointBillServiceId: number;
  periodId: number;
  donationServiceProviderId: IDonationData["donationServiceProviderId"];
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["jointBillMutation", "selectDonationBill"],
    mutationFn: (amount: number) =>
      selectDonationBill(
        jointBillServiceId,
        periodId,
        donationServiceProviderId,
        amount,
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            "jointBillServicePeriodData",
            jointBillServiceId,
            periodId,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicesExpectedPaymentAmount"],
        }),
      ]);
    },
  });
};

export const useUnselectDonationBillMutation = ({
  jointBillServiceId,
  periodId,
  donationServiceProviderId,
}: {
  jointBillServiceId: number;
  periodId: number;
  donationServiceProviderId: IDonationData["donationServiceProviderId"];
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["jointBillMutation", "unselectDonationBill"],
    mutationFn: () =>
      unselectDonationBill(
        jointBillServiceId,
        periodId,
        donationServiceProviderId,
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            "jointBillServicePeriodData",
            jointBillServiceId,
            periodId,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicesExpectedPaymentAmount"],
        }),
      ]);
    },
  });
};

export const useUpdateBillNumberMutation = ({
  jointBillServiceId,
  periodId,
  billId,
}: {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
  billId: IBillData["billId"];
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["jointBillMutation", "updateBillNumber"],
    mutationFn: (billNumber: string) => updateBillNumber(billId, billNumber),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: ["jointBillServicePeriodData", jointBillServiceId, periodId],
      }),
  });
};

export const useGetBillCertificateMutation = () =>
  useMutation({
    mutationFn: getBillCertificate,
  });
